import type { ReactNode } from 'react';
import React, { useEffect, useState } from 'react';

import { cn } from './helpers';

const TextareaContext = React.createContext<{
  remainingChars?: number;
  setRemainingChars: (maxLength?: number) => void;
  maxLength?: number;
} | null>(null);

function useTextarea() {
  const context = React.useContext(TextareaContext);
  if (!context) {
    throw new Error('useTextarea must be used within a <Textarea />');
  }
  return context;
}

const Textarea = ({
  maxLength,
  children,
}: {
  maxLength?: number;
  children: ReactNode;
}) => {
  const [remainingChars, setRemainingChars] = useState(maxLength);
  return (
    <TextareaContext.Provider
      value={{ remainingChars, maxLength, setRemainingChars }}
    >
      {children}
    </TextareaContext.Provider>
  );
};

const TextareaInput = ({
  hasError,
  className,
  value,
  onChange,
  ...props
}: Omit<React.ComponentProps<'textarea'>, 'maxLength'> & {
  hasError?: boolean;
}) => {
  const { setRemainingChars, maxLength } = useTextarea();

  useEffect(() => {
    if (value && maxLength) {
      setRemainingChars(maxLength - ((value as string).length ?? 0));
    }
  }, [maxLength, setRemainingChars, value]);

  return (
    <textarea
      {...(hasError && { 'data-error': 'error' })}
      className={cn(
        'text-gray-800 max-h-[300px] min-h-[100px] field-sizing-content w-full rounded-md border-[1px] border-pale-border bg-primary-30 px-3 py-2.5 outline-none outline-[1.5px] -outline-offset-[1.5px] transition-all placeholder:text-sm hover:border-primary-300',
        // disabledStyles
        'disabled:opacity-50 disabled:cursor-not-allowed disabled:hover:border-pale-border',
        // focusStyles
        'focus-within:ring-ring/15 focus-within:ring-[3px] focus:outline-primary-800 focus-visible:border-active-border',
        // errorStyles
        'data-error:border-destructive data-error:z-10 data-error:ring-[3px] data-error:ring-destructive/15 data-error:focus-within:border-destructive data-error:focus-within:ring-destructive/15',
        className
      )}
      maxLength={maxLength}
      onChange={(e) => {
        const newText = e.target.value;
        if (maxLength && newText.length > maxLength) return;
        if (maxLength) {
          setRemainingChars(maxLength - newText.length);
        }
        onChange?.(e);
      }}
      value={value}
      {...props}
    />
  );
};

const TextareaCharacterDisplay = ({
  className,
  renderLabel,
  ...props
}: React.ComponentProps<'span'> & {
  renderLabel?: (remainingCh?: number) => string | ReactNode;
}) => {
  const { remainingChars } = useTextarea();

  if (remainingChars === undefined) return;

  return (
    <span
      className={cn('text-gray-500 text-xs', className)}
      {...props}
    >
      {renderLabel?.(remainingChars) ??
        `Characters remaining : ${remainingChars}`}
    </span>
  );
};

export { Textarea, TextareaInput, TextareaCharacterDisplay };
